import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Button,
  Stack,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import RefreshIcon from "@mui/icons-material/Refresh";

import useAxiosPrivate from "app/hooks/useAxiosPrivate";
import { BASE_URL } from "app/api/axios";

/**
 * Universal Modal to preview Images and PDFs securely fetched from the backend.
 * Protected against document saving/downloading.
 *
 * @param {boolean} open - Modal open state
 * @param {function} onClose - Close handler
 * @param {object} doc - Document info { name, path, url, docType, type }
 */
export default function DocPreviewModal({ open, onClose, doc }) {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  // Image controls
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Extract display name
  const docName =
    doc?.name ||
    doc?.title ||
    (doc?.path ? doc.path.split("/").pop().split("\\").pop() : "Document");

  // Prevent keyboard shortcuts for saving (Ctrl+S / Cmd+S) and printing (Ctrl+P / Cmd+P)
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        e.stopPropagation();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open]);

  // Determine file format & load blob
  useEffect(() => {
    if (!open || !doc) {
      if (blobUrl && !doc?.url) {
        URL.revokeObjectURL(blobUrl);
      }
      setBlobUrl(null);
      setError(null);
      setZoom(1);
      setRotation(0);
      return;
    }

    setZoom(1);
    setRotation(0);
    setError(null);

    // If preloaded URL is supplied
    if (doc.url) {
      setBlobUrl(doc.url);
      const isPdfFormat =
        doc.docType?.toLowerCase() === "pdf" ||
        doc.name?.toLowerCase().endsWith(".pdf") ||
        doc.url.toLowerCase().includes(".pdf");
      setIsPdf(Boolean(isPdfFormat));
      setLoading(false);
      return;
    }

    // Otherwise fetch via authenticated API
    if (doc.path) {
      let isMounted = true;
      setLoading(true);
      setError(null);

      const fetchType = doc.type || "asset";
      const encodedPath = encodeURIComponent(doc.path);

      axiosPrivate
        .get(`${BASE_URL}/GetSecureDoc.php?type=${fetchType}&path=${encodedPath}`, {
          responseType: "blob",
        })
        .then((res) => {
          if (!isMounted) return;

          const contentType = res.headers["content-type"] || "";
          const detectedIsPdf =
            contentType.includes("pdf") ||
            doc.docType?.toLowerCase() === "pdf" ||
            doc.path?.toLowerCase().endsWith(".pdf") ||
            doc.name?.toLowerCase().endsWith(".pdf");

          setIsPdf(Boolean(detectedIsPdf));
          const url = URL.createObjectURL(res.data);
          setBlobUrl(url);
          setLoading(false);
        })
        .catch((err) => {
          if (!isMounted) return;
          console.error("Failed to load secure document:", err);
          setError(err.message || "Failed to fetch document");
          setLoading(false);
        });

      return () => {
        isMounted = false;
      };
    }
  }, [open, doc]);

  // Clean up object URL when unmounting or changing doc
  useEffect(() => {
    return () => {
      if (blobUrl && !doc?.url) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl, doc]);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3.5));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.4));
  const handleResetZoom = () => {
    setZoom(1);
    setRotation(0);
  };
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  const handleRetry = () => {
    if (doc) {
      setLoading(true);
      setError(null);
      const fetchType = doc.type || "asset";
      const encodedPath = encodeURIComponent(doc.path);

      axiosPrivate
        .get(`${BASE_URL}/GetSecureDoc.php?type=${fetchType}&path=${encodedPath}`, {
          responseType: "blob",
        })
        .then((res) => {
          const contentType = res.headers["content-type"] || "";
          const detectedIsPdf =
            contentType.includes("pdf") ||
            doc.docType?.toLowerCase() === "pdf" ||
            doc.path?.toLowerCase().endsWith(".pdf") ||
            doc.name?.toLowerCase().endsWith(".pdf");

          setIsPdf(Boolean(detectedIsPdf));
          const url = URL.createObjectURL(res.data);
          setBlobUrl(url);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Retry failed:", err);
          setError(err.message || "Failed to fetch document");
          setLoading(false);
        });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      onContextMenu={(e) => e.preventDefault()} // Disables right-click context menu
      PaperProps={{
        sx: {
          height: fullScreen ? "100vh" : "86vh",
          maxHeight: fullScreen ? "100vh" : "90vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: fullScreen ? 0 : 2,
          overflow: "hidden",
          backgroundColor: "#0f172a", // Deep dark background for rich preview contrast
          boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
          userSelect: "none", // Disables text/image selection
        },
      }}
    >
      {/* ── Dialog Header ─────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          m: 0,
          p: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#1e293b",
          color: "#f8fafc",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          flexShrink: 0,
        }}
      >
        {/* Left: Icon & Document Name */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, mr: 2 }}>
          {isPdf ? (
            <PictureAsPdfIcon sx={{ color: "#ef4444", fontSize: 22 }} />
          ) : (
            <ImageIcon sx={{ color: "#38bdf8", fontSize: 22 }} />
          )}

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: "0.95rem",
              color: "#f8fafc",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: { xs: 180, sm: 320, md: 500 },
            }}
            title={docName}
          >
            {docName}
          </Typography>

          <Chip
            size="small"
            label={isPdf ? "PDF (Protected)" : "Image (Protected)"}
            sx={{
              fontSize: "0.7rem",
              height: 20,
              fontWeight: 600,
              backgroundColor: isPdf ? "rgba(239, 68, 68, 0.15)" : "rgba(56, 189, 248, 0.15)",
              color: isPdf ? "#fca5a5" : "#bae6fd",
              border: `1px solid ${isPdf ? "rgba(239, 68, 68, 0.3)" : "rgba(56, 189, 248, 0.3)"}`,
            }}
          />
        </Stack>

        {/* Right: Controls & Actions */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Image-only zoom & rotate controls */}
          {!isPdf && blobUrl && !loading && (
            <>
              <Tooltip title="Zoom In">
                <span>
                  <IconButton size="small" onClick={handleZoomIn} sx={{ color: "#cbd5e1" }}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Zoom Out">
                <span>
                  <IconButton size="small" onClick={handleZoomOut} sx={{ color: "#cbd5e1" }}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Rotate">
                <span>
                  <IconButton size="small" onClick={handleRotate} sx={{ color: "#cbd5e1" }}>
                    <RotateRightIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {(zoom !== 1 || rotation !== 0) && (
                <Tooltip title="Reset View">
                  <span>
                    <IconButton size="small" onClick={handleResetZoom} sx={{ color: "#cbd5e1" }}>
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </>
          )}

          {/* Fullscreen toggle */}
          <Tooltip title={fullScreen ? "Exit Fullscreen" : "Fullscreen"}>
            <IconButton
              size="small"
              onClick={() => setFullScreen((prev) => !prev)}
              sx={{ color: "#cbd5e1" }}
            >
              {fullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Close modal */}
          <Tooltip title="Close Preview">
            <IconButton size="small" onClick={onClose} sx={{ color: "#f87171", ml: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      {/* ── Dialog Content / Viewer Body ───────────────────────────────────── */}
      <DialogContent
        onContextMenu={(e) => e.preventDefault()}
        sx={{
          p: 0,
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#090d16",
          position: "relative",
          overflow: isPdf ? "hidden" : "auto",
          userSelect: "none",
        }}
      >
        {/* Loading Spinner */}
        {loading && (
          <Stack spacing={2} alignItems="center" justifyContent="center">
            <CircularProgress size={42} sx={{ color: theme.palette.primary.main }} />
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              Loading document preview...
            </Typography>
          </Stack>
        )}

        {/* Error State */}
        {!loading && error && (
          <Box
            sx={{
              maxWidth: 420,
              p: 3,
              textAlign: "center",
              backgroundColor: "rgba(30, 41, 59, 0.7)",
              borderRadius: 2,
              border: "1px solid rgba(239, 68, 68, 0.3)",
            }}
          >
            <Typography variant="h6" sx={{ color: "#f87171", mb: 1, fontSize: "1rem" }}>
              Could not load preview
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2.5 }}>
              {error}
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRetry}
                sx={{ color: "#f8fafc", borderColor: "rgba(255, 255, 255, 0.2)" }}
              >
                Retry
              </Button>
            </Stack>
          </Box>
        )}

        {/* PDF Viewer — Toolbar hidden (#toolbar=0) to prevent native download/print */}
        {!loading && !error && blobUrl && isPdf && (
          <Box sx={{ width: "100%", height: "100%", backgroundColor: "#1e293b" }}>
            <iframe
              src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=1`}
              title={docName}
              width="100%"
              height="100%"
              style={{
                border: "none",
                display: "block",
              }}
            />
          </Box>
        )}

        {/* Image Viewer — Drag and context-menu disabled */}
        {!loading && !error && blobUrl && !isPdf && (
          <Box
            onContextMenu={(e) => e.preventDefault()}
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              overflow: "auto",
              cursor: zoom > 1 ? "grab" : "default",
              userSelect: "none",
            }}
          >
            <img
              src={blobUrl}
              alt={docName}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              style={{
                maxWidth: zoom <= 1 ? "100%" : "none",
                maxHeight: zoom <= 1 ? "100%" : "none",
                objectFit: "contain",
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: "center center",
                transition: "transform 0.15s ease-out",
                userSelect: "none",
                WebkitUserDrag: "none",
                pointerEvents: zoom > 1 ? "auto" : "none", // Prevents right click or drag when at normal size
                boxShadow: "0 8px 30px rgba(0, 0, 0, 0.5)",
                borderRadius: 4,
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
